import { useRef, type KeyboardEvent } from 'react';
import { ChevronRight, Info } from 'lucide-react';
import type { ActualsResponse, KpiCatalogueEntry, OpenEvidence, EvidenceScopeType } from '../../types/strategicKpis';
import { CATEGORY_LABEL, judgedOnYtdShare, kpiLabel, noDataText, paceBasisText, priorDirection, formatKpiValue, windowText } from '../../utils/strategicKpiFormat';
import { headSentence, judgeGroup, lightOf, paceAtYearEnd, steeredFamilies, steeredHead, topGroup, type JudgedValue, scopeLabel, hasBabRows } from '../../utils/strategicKpiSelectors';
import { BulletBar, BulletLegend } from './BulletBar';
import { PriorCue, StatusBadge, actualText, priorText, priorValueText, statusText, targetText } from './parts';
import { formatDate } from '../../utils/format';
import { useCardTableOffset } from './useTableOffset';

interface Props {
    data: ActualsResponse;
    /** Every steered KPI of the top node — the card head counts them all, whatever `family` shows. */
    steered: KpiCatalogueEntry[];
    /** The family pill: one category, or 'all' (default). Filters the rows only. */
    family?: string;
    onOpenEvidence?: OpenEvidence;
    /** The KPI whose evidence the page shows in the panel: its row is marked. */
    selectedKpiKey?: string | null;
}

const COLUMN_COUNT = 7;

/**
 * The card «Steered KPIs» (masterplan skpi-redesign E4, E8, E12): the head carries what the status hero kept —
 * «n of m behind plan» and the year progress — and the one table of the tab follows: per family one group row, per
 * steered KPI of the top node one row with the actual, what it is judged against (the YTD share and year value of a
 * visit target; the group target or Σ of the person targets — decision 1, both shown when both exist), the IBCS
 * bullet, the prior year as a neutral cue (never a difference, rule 8) and the status (neutral text, only the dot
 * is coloured, K4). A row opens the evidence of its number through `onOpenEvidence` (E9). Every figure is the
 * engine's; the page counts lights and formats.
 *
 * The `data-skpi-*` contract of scripts/skpi-compare.mjs (A0 method.md): the head element carries
 * `data-skpi-behind` / `data-skpi-judged`; every KPI row `data-skpi-kpi`, `-value`, `-target`, `-status`, `-prior`
 * — the same strings the cells show (the helpers of `parts.tsx`).
 */
export function SteeredKpiRows({ data, steered, family = 'all', onOpenEvidence, selectedKpiKey }: Props) {
    const card = useRef<HTMLElement>(null);
    const wrap = useRef<HTMLDivElement>(null);
    const offset = useCardTableOffset(card, wrap, [card, wrap]);
    const top = topGroup(data);
    const head = steeredHead(data, steered);
    if (!top || !head) return null;
    const yearPct = head.yearPct;
    const families = steeredFamilies(steered).filter(c => family === 'all' || c === family);
    const avg = data.kpis.find(k => k.kpiKey === 'visits.average_pct');
    const avgJudged = avg ? judgeGroup(avg, top.values[avg.kpiKey]) : null;
    const canOpen = onOpenEvidence && top.scopeType !== 'TOTAL';
    const scope = scopeLabel(top);
    const shown = family === 'all' ? null : CATEGORY_LABEL[family] ?? family;

    return (
        <section ref={card} className="card flush" aria-labelledby="skpi-steered-h" data-testid="steered-rows">
            <div className="card-head">
                <h2 id="skpi-steered-h">Steered KPIs</h2>
                <p className="sub" data-skpi-behind={head.behind} data-skpi-judged={head.judged}>
                    {headSentence(head)} · {top.personCount} {top.personCount === 1 ? 'person' : 'persons'} in the selection{shown && ` · the table shows ${shown}`}
                </p>
                <div className="end">
                    <span className="muted inline-flex items-center gap-2" title={`1 January to ${formatDate(data.asOf)}`}>
                        Year elapsed
                        <span className="relative inline-block overflow-hidden" style={{ width: 120, height: 6, borderRadius: 3, background: 'var(--surface-sunken)' }} aria-hidden="true">
                            <span className="absolute inset-y-0 left-0" style={{ width: `${yearPct}%`, background: 'var(--text-muted)', borderRadius: 3 }} />
                        </span>
                        <span className="num">{yearPct} %</span>
                    </span>
                </div>
            </div>
            <div ref={wrap} className="tbl-wrap" style={offset !== null ? ({ '--tbl-offset': `${offset}px`, borderLeft: 0, borderRight: 0, borderRadius: 0 } as React.CSSProperties) : { borderLeft: 0, borderRight: 0, borderRadius: 0 }}>
                <table className="tbl">
                    <thead>
                        <tr>
                            <th scope="col">KPI · window</th>
                            <th scope="col" className="n">Actual</th>
                            <th scope="col">Judged against</th>
                            <th scope="col">AC · PY · Plan</th>
                            <th scope="col">Prior year</th>
                            <th scope="col">Status</th>
                            <th scope="col"><span className="sr-only">Evidence</span></th>
                        </tr>
                    </thead>
                    <tbody>
                        {families.map(fam => {
                            const kpis = steered.filter(k => k.category === fam);
                            return [
                                <tr key={`grp-${fam}`} className="grp">
                                    <td colSpan={COLUMN_COUNT}>
                                        {CATEGORY_LABEL[fam] ?? fam}
                                        <span className="gsub">
                                            {kpis.length} {kpis.length === 1 ? 'KPI' : 'KPIs'} with a target
                                            {fam === 'visits' && <> · YTD to {formatDate(data.windows.visits.to)} · targets prorated by {yearPct} % of the year{data.windows.visitsPlanned ? ' · planned meetings shown separately' : ''}</>}
                                            {fam === 'gp' && kpis.some(judgedOnYtdShare) && <> · amounts judged against {yearPct} % of the year target</>}
                                            {fam === 'crm' && ' · rolling 12 months — a full year of data, judged against the year target'}
                                            {fam === 'marketdata' && kpis.some(k => paceAtYearEnd(k, data.windows.yearFraction)) && ' · year to date against the full-year target — judged at the year end, no light until then'}
                                        </span>
                                    </td>
                                </tr>,
                                ...kpis.map(k => (
                                    <Row
                                        key={k.kpiKey}
                                        kpi={k}
                                        judged={judgeGroup(k, top.values[k.kpiKey])}
                                        asOf={data.asOf}
                                        atYearEnd={paceAtYearEnd(k, data.windows.yearFraction)}
                                        scope={scope}
                                        selected={selectedKpiKey === k.kpiKey}
                                        onOpen={canOpen && k.groupAggregation !== 'derived' && (k.category !== 'bab' || hasBabRows(top)) ? () => onOpenEvidence(k.kpiKey, top.scopeType as EvidenceScopeType, top.scopeKey, scope, top.partial) : undefined}
                                    />
                                )),
                                fam === 'visits' && avg && avgJudged && (
                                    <tr key="avg">
                                        <td colSpan={COLUMN_COUNT} className="muted">
                                            <span className="strong">{avg.name}</span>{' '}
                                            <span className="strong num">{avgJudged.actual === null ? <span className="muted" title={noDataText(avgJudged.noData)}>no data</span> : formatKpiValue(avg.unit, avgJudged.actual)}</span>{' '}
                                            <span title={avg.definitionText}>mean of the class achievements with a target · definition version {avg.definitionVersion}</span>
                                        </td>
                                    </tr>
                                ),
                            ];
                        })}
                    </tbody>
                </table>
            </div>
            <div className="tbl-foot" style={{ padding: '12px 24px 16px' }}>
                <BulletLegend ytdShare={steered.some(k => judgedOnYtdShare(k) && k.category !== 'visits') ? 'visits and GP amounts' : 'visits'} />
                <span className="muted">Prior year is shown as a cue, never as a difference</span>
                {head.atYearEnd.length > 0 && (
                    <span data-testid="year-end-note">
                        Not counted until the year end: {head.atYearEnd.map(k => k.name).join(', ')} — a year-to-date count against the full-year target.
                    </span>
                )}
            </div>
        </section>
    );
}

function Row({ kpi, judged: j, asOf, atYearEnd, scope, selected, onOpen }: { kpi: KpiCatalogueEntry; judged: JudgedValue; asOf: string; atYearEnd: boolean; scope: string; selected: boolean; onOpen?: () => void }) {
    const isVisits = kpi.category === 'visits';
    const ytdShare = judgedOnYtdShare(kpi);
    const dir = priorDirection(kpi, j.actual, j.prior);
    const light = lightOf(j);
    const source = j.hasGroupTarget ? 'group target' : 'Σ person targets';
    const name = kpiLabel(kpi);
    // A click that ends a text selection (copying a figure to Excel) is not a click on the row.
    const open = onOpen ? () => { if (window.getSelection()?.toString()) return; onOpen(); } : undefined;
    const onKey = onOpen ? (e: KeyboardEvent<HTMLTableRowElement>) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); }
    } : undefined;
    return (
        <tr
            className={selected ? 'selected' : undefined}
            aria-current={selected ? 'true' : undefined}
            tabIndex={onOpen ? 0 : undefined}
            onClick={open}
            onKeyDown={onKey}
            style={onOpen ? { cursor: 'pointer' } : undefined}
            title={onOpen ? `Open the rows behind ${name} of ${scope}` : undefined}
            data-skpi-kpi={kpi.kpiKey}
            data-skpi-value={actualText(kpi, j)}
            data-skpi-target={targetText(kpi, j)}
            data-skpi-status={statusText({ light, achievement: j.achievement, hasTarget: j.hasTarget, atYearEnd })}
            data-skpi-prior={priorText(kpi, j)}
        >
            <td>
                <div className="cellstack" style={{ maxWidth: 300 }}>
                    <span className="name" title={`${name} — ${kpi.definitionText}`}>{name}</span>
                    <span className="sub2" title={kpi.window.text}>
                        {windowText(kpi, kpi.window)}{kpi.windowType !== 'POINT' && <> · as of {formatDate(asOf)}</>}{kpi.direction === 'lower' && ' · lower is better'}
                    </span>
                </div>
            </td>
            <td className="n">
                <div className="cellstack items-end">
                    {j.actual === null
                        ? <span className="muted inline-flex items-center gap-1" title={noDataText(j.noData)}>{actualText(kpi, j)} <Info className="i sm" aria-hidden="true" /></span>
                        : <span className="strong">{actualText(kpi, j)}</span>}
                    {isVisits && j.planned !== undefined && j.planned > 0 && (
                        <span className="sub2" title="Meetings scheduled after the as-of date up to the month end — shown separately, not part of the actual (definition version 2)" data-planned>+ {j.planned} planned</span>
                    )}
                </div>
            </td>
            <td>
                <div className="cellstack">
                    <span className="strong">{targetText(kpi, j)}</span>
                    <span className="sub2">{source} · {paceBasisText(kpi)}</span>
                    {j.hasGroupTarget && j.personsTarget !== null && (
                        <span className="sub2" title="Σ of the person targets — stored independently of the group target and may differ (decision 1)">Σ persons {formatKpiValue(kpi.unit, j.personsTarget)}</span>
                    )}
                </div>
            </td>
            <td>
                <BulletBar unit={kpi.unit} actual={j.actual} planned={j.planned} prior={j.prior} targetYtd={j.targetYtd} targetYear={j.target} ytdShare={ytdShare} lowerIsBetter={kpi.direction === 'lower'} />
            </td>
            <td title={windowText(kpi, kpi.priorWindow)}>
                <div className="cellstack">
                    <span><span className="muted">PY</span> {priorValueText(kpi, j)}</span>
                    {dir && <span className="sub2"><PriorCue dir={dir} /></span>}
                </div>
            </td>
            <td>
                <StatusBadge
                    light={light}
                    achievement={j.achievement}
                    hasTarget={j.hasTarget}
                    noData={j.noData}
                    atYearEnd={atYearEnd}
                    title={j.hasTarget && j.achievement !== null && !atYearEnd ? `Achievement vs ${source}${ytdShare ? ' (YTD share)' : ''}` : undefined}
                />
            </td>
            <td className="n">
                {onOpen && <ChevronRight className="i sm muted" aria-hidden="true" />}
            </td>
        </tr>
    );
}
